// Searchable model dropdown for one AI task: the two recommendations on top ("Best performance",
// "Cheapest that works"), then every OpenRouter model with its price and what 1,000 actions cost.

import {
  AI_TASK_INFO,
  estimateActionCost,
  formatPerMillion,
  formatUsd,
  type AiModelInfo,
  type AiRecommendation,
  type AiRecommendationKind,
  type AiTask,
} from "@gmx/shared/features/ai";
import { Badge, cx, GfPortal, Spinner, useAnchoredPosition, useOutsidePointer } from "@gmx/ui";
import { Check, ChevronDown, Clapperboard, Eye, Search } from "lucide-react";
import { useId, useMemo, useRef, useState, type KeyboardEvent } from "react";

export const REC_LABEL: Record<AiRecommendationKind, string> = { best: "Best performance", cheapest: "Cheapest that works" };
const REC_TONE = { best: "accent", cheapest: "success" } as const;

type SortMode = "vendor" | "price" | "newest";
const SORTS: { value: SortMode; label: string }[] = [
  { value: "vendor", label: "Provider" },
  { value: "price", label: "Cheapest" },
  { value: "newest", label: "Newest" },
];

/** "≈ $0.24 per 1,000 DMs" */
export function per1k(m: AiModelInfo, task: AiTask): string {
  const usd = estimateActionCost(m, task) * 1000;
  return usd <= 0 ? "Free" : `≈ ${formatUsd(usd)} per 1,000 ${AI_TASK_INFO[task].unit}`;
}

const VENDOR_LABEL: Record<string, string> = {
  anthropic: "Anthropic (Claude)",
  openai: "OpenAI (GPT)",
  google: "Google (Gemini)",
  "x-ai": "xAI (Grok)",
  "meta-llama": "Meta (Llama)",
  mistralai: "Mistral",
  deepseek: "DeepSeek",
  qwen: "Qwen",
  moonshotai: "Moonshot (Kimi)",
  "z-ai": "Z.ai (GLM)",
};
const vendorLabel = (v: string) => VENDOR_LABEL[v] ?? v;

interface Option {
  key: string;
  model: AiModelInfo;
  rec?: AiRecommendationKind;
  why?: string;
  /** First option of a vendor group (renders a group heading above it). */
  heading?: string;
}

export interface ModelPickerProps {
  task: AiTask;
  value: string | null;
  models: AiModelInfo[] | undefined;
  recommendations: AiRecommendation[] | undefined;
  loading?: boolean;
  disabled?: boolean;
  saving?: boolean;
  onChange: (id: string) => void;
  /** Accessible name, e.g. "Messaging model". */
  label: string;
}

export function ModelPicker({ task, value, models, recommendations, loading, disabled, saving, onChange, label }: ModelPickerProps) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortMode>("vendor");
  const [imagesOnly, setImagesOnly] = useState(false);
  const [active, setActive] = useState(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const needsVision = AI_TASK_INFO[task].needsVision;
  const needsVideo = AI_TASK_INFO[task].needsVideo === true;

  useAnchoredPosition(anchorRef, panelRef, open, { align: "start", side: "bottom", offset: 6 });
  useOutsidePointer([anchorRef, panelRef], open, () => setOpen(false));

  const byId = useMemo(() => new Map((models ?? []).map((m) => [m.id, m])), [models]);
  const current = value ? byId.get(value) : undefined;
  const currentRec = recommendations?.find((r) => r.modelId === value)?.kind;

  const options = useMemo<Option[]>(() => {
    const all = models ?? [];
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const matches = (m: AiModelInfo) => {
      if ((needsVision || imagesOnly) && !m.vision) return false;
      if (needsVideo && !m.video) return false;
      const hay = `${m.name} ${m.id}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    };
    const recs: Option[] = terms.length
      ? []
      : (recommendations ?? []).flatMap((r) => {
          const m = byId.get(r.modelId);
          return m ? [{ key: `rec-${r.kind}`, model: m, rec: r.kind, why: r.why }] : [];
        });
    const list = all.filter(matches);
    if (sort === "price") list.sort((a, b) => estimateActionCost(a, task) - estimateActionCost(b, task) || a.name.localeCompare(b.name));
    else if (sort === "newest") list.sort((a, b) => b.createdAt - a.createdAt);
    let lastVendor = "";
    const rest = list.map((m): Option => {
      const heading = sort === "vendor" && m.vendor !== lastVendor ? vendorLabel(m.vendor) : undefined;
      lastVendor = m.vendor;
      return { key: m.id, model: m, heading };
    });
    return [...recs, ...rest];
  }, [models, recommendations, byId, q, sort, imagesOnly, needsVision, needsVideo, task]);

  function openPanel() {
    if (disabled) return;
    setQ("");
    const idx = options.findIndex((o) => !o.rec && o.model.id === value);
    setActive(Math.max(0, idx));
    setOpen(true);
    window.setTimeout(() => {
      inputRef.current?.focus();
      listRef.current?.querySelector<HTMLElement>("[aria-selected='true']")?.scrollIntoView({ block: "center" });
    }, 0);
  }

  function choose(o: Option | undefined) {
    if (!o) return;
    setOpen(false);
    anchorRef.current?.querySelector("button")?.focus();
    if (o.model.id !== value) onChange(o.model.id);
  }

  function move(delta: number) {
    if (!options.length) return;
    const next = (active + delta + options.length) % options.length;
    setActive(next);
    document.getElementById(`${uid}-opt-${next}`)?.scrollIntoView({ block: "nearest" });
  }

  function onKey(e: KeyboardEvent<HTMLElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(options[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      anchorRef.current?.querySelector("button")?.focus();
    }
  }

  const listCount = options.filter((o) => !o.rec).length;

  return (
    <div ref={anchorRef} className="gfp-mp">
      <button
        type="button"
        className={cx("gfp-mp__trigger", open && "gfp-mp__trigger--open")}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current?.name ?? value ?? "none chosen"}`}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPanel())}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            openPanel();
          }
        }}
      >
        <span className="gfp-mp__current">
          <span className="gfp-mp__name">
            {current?.name ?? (value ? value : loading ? "Loading models…" : "Choose a model")}
            {currentRec ? (
              <Badge size="sm" tone={REC_TONE[currentRec]}>
                {REC_LABEL[currentRec]}
              </Badge>
            ) : null}
          </span>
          {current ? <span className="gfp-mp__sub">{per1k(current, task)}</span> : null}
        </span>
        {saving ? <Spinner size={16} /> : <ChevronDown size={18} aria-hidden="true" className="gfp-mp__chev" />}
      </button>

      {open ? (
        <GfPortal>
          <div ref={panelRef} className="gfp-mp__panel" style={{ top: -9999, left: -9999 }} onKeyDown={onKey}>
            <div className="gfp-mp__search">
              <Search size={16} aria-hidden="true" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActive(0);
                }}
                placeholder={`Search ${models?.length ?? ""} models — "claude", "gemini flash", "gpt"…`}
                role="combobox"
                aria-expanded="true"
                aria-controls={`${uid}-list`}
                aria-activedescendant={options[active] ? `${uid}-opt-${active}` : undefined}
                aria-label={`Search models for ${label}`}
              />
            </div>
            <div className="gfp-mp__tools">
              <div className="gfp-mp__sort" role="group" aria-label="Sort models">
                {SORTS.map((s) => (
                  <button key={s.value} type="button" aria-pressed={sort === s.value} className={cx(sort === s.value && "is-on")} onClick={() => setSort(s.value)}>
                    {s.label}
                  </button>
                ))}
              </div>
              {needsVideo ? (
                <span className="gfp-mp__note">
                  <Clapperboard size={14} aria-hidden="true" /> Only models that can watch video
                </span>
              ) : needsVision ? (
                <span className="gfp-mp__note">
                  <Eye size={14} aria-hidden="true" /> Only models that can see images
                </span>
              ) : (
                <label className="gfp-mp__check">
                  <input type="checkbox" checked={imagesOnly} onChange={(e) => setImagesOnly(e.target.checked)} /> Sees images
                </label>
              )}
            </div>

            <div ref={listRef} id={`${uid}-list`} role="listbox" aria-label={label} className="gfp-mp__list">
              {loading && !models ? (
                <div className="gfp-mp__empty">
                  <Spinner size={18} /> Loading OpenRouter's model list…
                </div>
              ) : options.length === 0 ? (
                <div className="gfp-mp__empty">No models match “{q}”.</div>
              ) : (
                options.map((o, i) => {
                  const m = o.model;
                  const selected = !o.rec && m.id === value;
                  return (
                    <div key={o.key}>
                      {o.rec && i === 0 ? <div className="gfp-mp__group">Recommended for {AI_TASK_INFO[task].label.toLowerCase()}</div> : null}
                      {!o.rec && (i === 0 || options[i - 1]?.rec) && sort !== "vendor" ? (
                        <div className="gfp-mp__group">All models ({listCount})</div>
                      ) : null}
                      {o.heading ? <div className="gfp-mp__group">{o.heading}</div> : null}
                      <div
                        id={`${uid}-opt-${i}`}
                        role="option"
                        aria-selected={selected}
                        className={cx("gfp-mp__opt", i === active && "is-active", o.rec && "gfp-mp__opt--rec", selected && "is-selected")}
                        onMouseEnter={() => setActive(i)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => choose(o)}
                      >
                        <span className="gfp-mp__check-col">{m.id === value ? <Check size={16} aria-hidden="true" /> : null}</span>
                        <span className="gfp-mp__main">
                          <span className="gfp-mp__row1">
                            <span className="gfp-mp__optname">{m.name}</span>
                            {o.rec ? (
                              <Badge size="sm" tone={REC_TONE[o.rec]}>
                                {REC_LABEL[o.rec]}
                              </Badge>
                            ) : null}
                            {m.vision ? (
                              <Badge size="sm" tone="info" icon={<Eye size={12} />}>
                                Sees images
                              </Badge>
                            ) : null}
                            {m.video && needsVideo ? (
                              <Badge size="sm" tone="info" icon={<Clapperboard size={12} />}>
                                Watches video
                              </Badge>
                            ) : null}
                            {m.free ? (
                              <Badge size="sm" tone="success">
                                Free
                              </Badge>
                            ) : null}
                          </span>
                          {o.why ? <span className="gfp-mp__why">{o.why}</span> : <span className="gfp-mp__id">{m.id}</span>}
                        </span>
                        <span className="gfp-mp__price">
                          <span className="gfp-mp__est">{per1k(m, task)}</span>
                          <span className="gfp-mp__rate">
                            {formatPerMillion(m.inputPrice)} in · {formatPerMillion(m.outputPrice)} out / 1M
                          </span>
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="gfp-mp__foot">
              Prices are OpenRouter's, per million tokens. “Per 1,000” figures are estimates for a typical {AI_TASK_INFO[task].unit.replace(/s$/, "")}.
            </div>
          </div>
        </GfPortal>
      ) : null}
    </div>
  );
}
